/**
 * Builds the written report as a PDF.
 *
 * pdf-lib has no layout engine, so this file provides the small amount of one the
 * report needs: a cursor that knows when to break the page, wrapped paragraphs,
 * key/value blocks and tables that repeat their header across a break.
 *
 * The standard Helvetica fonts are used deliberately - no font embedding, so the
 * file stays small and portable. Those fonts are WinAnsi encoded and pdf-lib
 * throws on any character outside that set, so every string is put through
 * `sanitise` before it is drawn. Report text comes from client records and email
 * subject lines, which routinely contain characters that would otherwise fail.
 */

import { PDFDocument, StandardFonts, rgb } from "pdf-lib"
import { earliestSignup, subscriptionSummary } from "./workbook-out"

const PAGE_WIDTH = 595.28
const PAGE_HEIGHT = 841.89
const MARGIN = 46
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2
const FOOTER_HEIGHT = 34

const INK = rgb(0.11, 0.12, 0.14)
const MUTED = rgb(0.42, 0.45, 0.5)
const LINE = rgb(0.85, 0.87, 0.89)
const HEADER_FILL = rgb(0.94, 0.95, 0.96)
const STRIPE_FILL = rgb(0.975, 0.98, 0.985)
const ACCENT = rgb(0.05, 0.36, 0.24)

/**
 * Characters that appear constantly in real data and have a plain equivalent.
 */
const REPLACEMENTS = [
	[/[‘’‚‛′]/g, "'"],
	[/[“”„‟″]/g, '"'],
	[/[‐‑‒–—―]/g, "-"],
	[/[…]/g, "..."],
	[/[   ]/g, " "],
	[/[•●▪]/g, "-"],
	[/[™]/g, "(TM)"],
	[/[®]/g, "(R)"],
]

/**
 * Reduce a string to what the standard WinAnsi fonts can draw.
 */
const sanitise = (value) => {
	let text = value === null || value === undefined ? "" : String(value)

	REPLACEMENTS.forEach(([pattern, replacement]) => {
		text = text.replace(pattern, replacement)
	})

	let output = ""

	for (const character of text) {
		const code = character.codePointAt(0)

		if (code === 10) {
			output += "\n"
			continue
		}

		// Printable ASCII, or Latin-1 which WinAnsi covers, or the euro sign
		if ((code >= 32 && code <= 126) || (code >= 160 && code <= 255) || code === 8364) {
			output += character
			continue
		}

		if (code > 126) output += "?"
	}

	return output
}

export const formatMoney = (value) => {
	const numeric = Number(value)

	if (!numeric && numeric !== 0) return ""

	return `£${numeric.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * ISO timestamps are what the lookups produce; the report shows them as a date
 * and time a reader can take in, and keeps the full value in the spreadsheet.
 */
export const formatDateTime = (value) => {
	if (!value) return ""

	const parsed = new Date(value)

	if (Number.isNaN(parsed.getTime())) return String(value)

	return parsed.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

/**
 * Numeric forms for table cells. Table columns are narrow and a truncated date is
 * useless, so inside tables dates are shown as DD/MM/YYYY.
 */
export const formatShortDate = (value) => {
	if (!value) return ""

	const parsed = new Date(value)

	if (Number.isNaN(parsed.getTime())) return String(value)

	return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
}

export const formatShortDateTime = (value) => {
	if (!value) return ""

	const parsed = new Date(value)

	if (Number.isNaN(parsed.getTime())) return String(value)

	return `${formatShortDate(value)} ${parsed.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
}

/**
 * Minimal layout engine over a pdf-lib document.
 */
class Writer {
	constructor(document, fonts) {
		this.document = document
		this.fonts = fonts
		this.pages = []
		this.newPage()
	}

	newPage() {
		this.page = this.document.addPage([PAGE_WIDTH, PAGE_HEIGHT])
		this.pages.push(this.page)
		this.y = PAGE_HEIGHT - MARGIN
	}

	/** Break to a new page unless `height` still fits above the footer */
	ensure(height) {
		if (this.y - height < MARGIN + FOOTER_HEIGHT) this.newPage()
	}

	space(height) {
		this.y -= height
	}

	widthOf(text, size, bold) {
		return (bold ? this.fonts.bold : this.fonts.regular).widthOfTextAtSize(text, size)
	}

	/** Split text into lines that fit `maxWidth`, honouring existing newlines */
	wrap(text, size, maxWidth, bold) {
		const font = bold ? this.fonts.bold : this.fonts.regular
		const lines = []

		String(text).split("\n").forEach((paragraph) => {
			const words = paragraph.split(/\s+/).filter(Boolean)

			if (words.length === 0) {
				lines.push("")
				return
			}

			let current = ""

			words.forEach((word) => {
				const candidate = current ? `${current} ${word}` : word

				if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
					current = candidate
					return
				}

				if (current) lines.push(current)

				// A single word longer than the line has to be broken by character
				if (font.widthOfTextAtSize(word, size) > maxWidth) {
					let chunk = ""

					for (const character of word) {
						if (font.widthOfTextAtSize(chunk + character, size) > maxWidth) {
							lines.push(chunk)
							chunk = character
							continue
						}

						chunk += character
					}

					current = chunk
					return
				}

				current = word
			})

			if (current) lines.push(current)
		})

		return lines
	}

	/** Shorten to one line, with an ellipsis, for table cells */
	truncate(text, size, maxWidth, bold) {
		const clean = sanitise(text).replace(/\n/g, " ")

		if (this.widthOf(clean, size, bold) <= maxWidth) return clean

		let output = clean

		while (output.length > 1 && this.widthOf(`${output}...`, size, bold) > maxWidth) {
			output = output.slice(0, -1)
		}

		return `${output}...`
	}

	text(value, { size = 10, bold = false, colour = INK, x = MARGIN, maxWidth = CONTENT_WIDTH, leading = 1.35 } = {}) {
		const lines = this.wrap(sanitise(value), size, maxWidth, bold)
		const lineHeight = size * leading

		lines.forEach((line) => {
			this.ensure(lineHeight)
			this.page.drawText(line, { x, y: this.y - size, size, font: bold ? this.fonts.bold : this.fonts.regular, color: colour })
			this.y -= lineHeight
		})
	}

	title(value) {
		this.ensure(30)
		this.text(value, { size: 22, bold: true })
		this.space(6)
	}

	heading(value) {
		this.ensure(34)
		this.space(10)
		this.text(value, { size: 13, bold: true, colour: ACCENT })
		this.space(2)
		this.rule()
		this.space(6)
	}

	subheading(value) {
		this.ensure(22)
		this.space(6)
		this.text(value, { size: 10.5, bold: true })
		this.space(2)
	}

	rule() {
		this.ensure(6)
		this.page.drawLine({ start: { x: MARGIN, y: this.y }, end: { x: PAGE_WIDTH - MARGIN, y: this.y }, thickness: 0.75, color: LINE })
		this.y -= 4
	}

	/**
	 * Two-column block of labelled values. Labels wrap rather than truncate -
	 * a clipped label in a report that has to stand on its own is not acceptable.
	 */
	keyValues(rows) {
		const labelWidth = 210
		const gutter = 12
		const valueWidth = CONTENT_WIDTH - labelWidth - gutter
		const lineHeight = 13.5

		rows.filter(Boolean).forEach(([label, value]) => {
			const labelLines = this.wrap(sanitise(label), 10, labelWidth, true)
			const valueLines = this.wrap(sanitise(value === 0 ? "0" : value || "-"), 10, valueWidth, false)
			const blockHeight = Math.max(labelLines.length, valueLines.length, 1) * lineHeight

			this.ensure(blockHeight)

			const top = this.y

			labelLines.forEach((line, index) => {
				this.page.drawText(line, { x: MARGIN, y: top - 10 - index * lineHeight, size: 10, font: this.fonts.bold, color: MUTED })
			})

			valueLines.forEach((line, index) => {
				this.page.drawText(line, { x: MARGIN + labelWidth + gutter, y: top - 10 - index * lineHeight, size: 10, font: this.fonts.regular, color: INK })
			})

			this.y = top - blockHeight
		})
	}

	/**
	 * A table. Columns carry a fractional `width`; rows are arrays of strings.
	 * The header is redrawn whenever the table crosses a page break.
	 */
	table(columns, rows, { emptyMessage = "None found" } = {}) {
		if (rows.length === 0) {
			this.text(emptyMessage, { size: 9.5, colour: MUTED })
			this.space(4)
			return
		}

		const totalUnits = columns.reduce((total, column) => total + column.width, 0)
		const widths = columns.map((column) => (column.width / totalUnits) * CONTENT_WIDTH)
		const rowHeight = 16
		const size = 8.5

		const drawHeader = () => {
			this.ensure(rowHeight * 2)
			this.page.drawRectangle({ x: MARGIN, y: this.y - rowHeight, width: CONTENT_WIDTH, height: rowHeight, color: HEADER_FILL })

			let x = MARGIN

			columns.forEach((column, index) => {
				this.page.drawText(this.truncate(column.label, size, widths[index] - 8, true), { x: x + 4, y: this.y - rowHeight + 5, size, font: this.fonts.bold, color: INK })
				x += widths[index]
			})

			this.y -= rowHeight
		}

		drawHeader()

		rows.forEach((row, rowIndex) => {
			if (this.y - rowHeight < MARGIN + FOOTER_HEIGHT) {
				this.newPage()
				drawHeader()
			}

			if (rowIndex % 2 === 1) {
				this.page.drawRectangle({ x: MARGIN, y: this.y - rowHeight, width: CONTENT_WIDTH, height: rowHeight, color: STRIPE_FILL })
			}

			let x = MARGIN

			row.forEach((cell, index) => {
				const column = columns[index]
				const value = this.truncate(cell === 0 ? "0" : cell || "", size, widths[index] - 8, false)
				const cellX = column?.align === "right" ? x + widths[index] - 4 - this.widthOf(value, size, false) : x + 4

				this.page.drawText(value, { x: cellX, y: this.y - rowHeight + 5, size, font: this.fonts.regular, color: INK })
				x += widths[index]
			})

			this.y -= rowHeight
			this.page.drawLine({ start: { x: MARGIN, y: this.y }, end: { x: PAGE_WIDTH - MARGIN, y: this.y }, thickness: 0.4, color: LINE })
		})

		this.space(6)
	}

	bullets(items) {
		items.filter(Boolean).forEach((item) => {
			const lines = this.wrap(sanitise(item), 10, CONTENT_WIDTH - 14, false)

			lines.forEach((line, index) => {
				this.ensure(14)
				this.page.drawText(index === 0 ? "-" : "", { x: MARGIN, y: this.y - 10, size: 10, font: this.fonts.regular, color: MUTED })
				this.page.drawText(line, { x: MARGIN + 12, y: this.y - 10, size: 10, font: this.fonts.regular, color: INK })
				this.y -= 14
			})
		})

		this.space(4)
	}

	/** Page numbers and the confidentiality marking, drawn once at the end */
	finish(caseReference) {
		const total = this.pages.length

		this.pages.forEach((page, index) => {
			const left = sanitise(`${caseReference} - confidential`)
			const right = `Page ${index + 1} of ${total}`

			page.drawLine({ start: { x: MARGIN, y: MARGIN + 18 }, end: { x: PAGE_WIDTH - MARGIN, y: MARGIN + 18 }, thickness: 0.5, color: LINE })
			page.drawText(left, { x: MARGIN, y: MARGIN + 6, size: 8, font: this.fonts.regular, color: MUTED })
			page.drawText(right, { x: PAGE_WIDTH - MARGIN - this.fonts.regular.widthOfTextAtSize(right, 8), y: MARGIN + 6, size: 8, font: this.fonts.regular, color: MUTED })
		})
	}
}

/**
 * One address's section of the report.
 */
const writeSubject = (writer, result) => {
	writer.newPage()
	writer.title(result.email)

	if (result.status === "ERROR") {
		writer.text(`This address could not be completed: ${result.error}`, { size: 10, bold: true })
		writer.space(6)
	}

	const memberships = result.mailchimp?.memberships || []
	const activity = result.mailchimp?.activity || []
	const liveBookings = result.bookings.filter((booking) => booking.state === "Live")

	/**
	 * An address whose lookup failed part way through must never be described as
	 * having nothing on record. Where a section is empty because the checks did not
	 * finish, it says so instead of asserting a negative.
	 */
	const incomplete = result.status === "ERROR"
	const noneFound = (statement) => (incomplete ? "Not established - the checks for this address did not complete." : statement)

	writer.keyValues([
		["Source", result.source],
		result.spellings.length > 1 && ["Spellings in source", result.spellings.join(", ")],
		result.duplicateOf.length > 0 && ["Repeated in source at", result.duplicateOf.join(", ")],
		["Marketing subscription", subscriptionSummary(result)],
		["First added to a mailing list", result.mailchimpSkipped ? "-" : formatDateTime(earliestSignup(memberships)) || (incomplete ? "Not established" : "No signup date recorded")],
		["Mailing lists held on", memberships.length > 0 ? memberships.map((membership) => `${membership.audienceName} (${membership.status})`).join("; ") : incomplete ? "Not established" : "None"],
		["Marketing events on record", result.mailchimpSkipped ? "-" : incomplete && activity.length === 0 ? "Not established" : String(activity.length)],
		["Booking system accounts", result.clients.length > 0 ? result.clients.map((client) => client.id).join(", ") : incomplete ? "Not established" : "None"],
		["Enquiries made", incomplete && result.enquiries.length === 0 ? "Not established" : String(result.enquiries.length)],
		["Bookings made", incomplete && result.bookings.length === 0 ? "Not established" : `${result.bookings.length} (${liveBookings.length} live)`],
		["Quotes issued", incomplete && result.quotes.length === 0 ? "Not established" : String(result.quotes.length)],
		["Lifetime value", incomplete && result.bookings.length === 0 ? "Not established" : formatMoney(result.totals.lifetimeValue)],
		["Paid by client", incomplete && result.bookings.length === 0 ? "Not established" : formatMoney(result.totals.paidByClient)],
		["Emails on file", !result.correspondence.accessible ? "Not accessible to this account" : incomplete && result.correspondence.messages.length === 0 ? "Not established" : String(result.correspondence.messages.length)],
		...Object.entries(result.context || {}).map(([label, value]) => [`Source file: ${label}`, value]),
	])

	// Marketing
	writer.heading("Marketing and subscription history")

	if (result.mailchimpSkipped) {
		writer.text("No Mailchimp key was supplied for this run, so no marketing records were retrieved for this address.", { size: 9.5, colour: MUTED })
	} else if (result.mailchimp?.error) {
		writer.text(`Mailchimp could not be searched for this address: ${result.mailchimp.error}`, { size: 9.5, colour: MUTED })
	} else {
		writer.subheading("Mailing list membership")
		writer.table(
			[
				{ label: "Audience", width: 2 },
				{ label: "Status", width: 1.35 },
				{ label: "Arch.", width: 0.85 },
				{ label: "Signed up", width: 1.15 },
				{ label: "Opted in", width: 1.15 },
				{ label: "Changed", width: 1.2 },
				{ label: "Source", width: 2 },
				{ label: "Tags", width: 1.2 },
			],
			memberships.map((membership) => [
				membership.audienceName,
				membership.status,
				membership.archived ? "Yes" : "No",
				formatShortDate(membership.signedUp),
				formatShortDate(membership.optedIn),
				formatShortDate(membership.lastChanged),
				membership.source,
				membership.tags.join(", "),
			]),
			{ emptyMessage: noneFound("This address was not found in any Mailchimp audience on the account.") }
		)

		const unsubscribed = memberships.filter((membership) => membership.unsubscribeReason)

		if (unsubscribed.length > 0) {
			writer.subheading("Unsubscribe reasons given")
			writer.bullets(unsubscribed.map((membership) => `${membership.audienceName}: ${membership.unsubscribeReason}`))
		}

		const tagHistory = memberships.flatMap((membership) => (membership.tagDetail || []).map((tag) => [membership.audienceName, tag.name, formatShortDate(tag.added)]))

		if (tagHistory.length > 0) {
			writer.subheading("Tags applied")
			writer.table(
				[
					{ label: "Audience", width: 2.4 },
					{ label: "Tag", width: 3 },
					{ label: "Date applied", width: 1.6 },
				],
				tagHistory
			)
		}

		writer.subheading("Marketing communications recorded")
		writer.table(
			[
				{ label: "Date", width: 1.7 },
				{ label: "Event", width: 1.1 },
				{ label: "Campaign", width: 2.6 },
				{ label: "Subject line", width: 3 },
				{ label: "Audience", width: 1.6 },
			],
			activity.map((event) => [formatShortDateTime(event.timestamp), event.action, event.campaignTitle || event.campaignID, event.campaignSubject, event.audienceName]),
			{ emptyMessage: noneFound("Mailchimp holds no recorded campaign activity for this address.") }
		)
	}

	// Booking system
	writer.heading("Booking system records")

	writer.subheading("Client accounts")
	writer.table(
		[
			{ label: "Client ID", width: 2.2 },
			{ label: "Name", width: 2 },
			{ label: "Email on account", width: 2.8 },
			{ label: "Phone", width: 1.5 },
			{ label: "Created", width: 1.3 },
		],
		result.clients.map((client) => [client.id, [client.firstName, client.lastName].filter(Boolean).join(" "), client.email, client.phone, formatShortDate(client.created)]),
		{ emptyMessage: noneFound("No client account exists for this address.") }
	)

	writer.subheading("Enquiries")
	writer.table(
		[
			{ label: "Raised", width: 1.15 },
			{ label: "State", width: 0.95 },
			{ label: "Destination", width: 1.9 },
			{ label: "Travel date", width: 1.15 },
			{ label: "Size", width: 0.65, align: "right" },
			{ label: "Quoted", width: 1.1, align: "right" },
			{ label: "Status", width: 1.35 },
			{ label: "Channel", width: 1.95 },
		],
		result.enquiries.map((enquiry) => [
			formatShortDate(enquiry.created),
			enquiry.state,
			enquiry.location,
			formatShortDate(enquiry.travelDate),
			enquiry.groupSize === null ? "" : String(enquiry.groupSize),
			enquiry.quoted === null ? "" : formatMoney(enquiry.quoted),
			enquiry.status,
			enquiry.inboundChannel,
		]),
		{ emptyMessage: noneFound("No enquiries are held for this address.") }
	)

	writer.subheading("Bookings")
	writer.table(
		[
			{ label: "Reference", width: 1.2 },
			{ label: "Booked", width: 1.15 },
			{ label: "Travel date", width: 1.15 },
			{ label: "Destination", width: 1.6 },
			{ label: "State", width: 1 },
			{ label: "Holiday cost", width: 1.55, align: "right" },
			{ label: "Paid", width: 1.15, align: "right" },
			{ label: "Cost field", width: 2.2 },
		],
		result.bookings.map((booking) => [
			booking.reference,
			formatShortDate(booking.booked || booking.created),
			formatShortDate(booking.travelDate),
			booking.location,
			booking.state,
			formatMoney(booking.customerPrice),
			formatMoney(booking.paidByClient),
			booking.customerPriceSource,
		]),
		{ emptyMessage: noneFound("No bookings are held for this address.") }
	)

	if (result.bookings.length > 0) {
		writer.keyValues([
			["Lifetime value", `${formatMoney(result.totals.lifetimeValue)} across ${result.bookings.length} booking(s)`],
			["Of which live bookings", formatMoney(result.totals.liveLifetimeValue)],
			["Paid by client", formatMoney(result.totals.paidByClient)],
			["Payments recorded against bookings", formatMoney(result.totals.paymentsRecorded)],
		])
	}

	if (result.quotes.length > 0) {
		writer.subheading("Quotes")
		writer.table(
			[
				{ label: "Created", width: 1.5 },
				{ label: "Kind", width: 1.4 },
				{ label: "Destination", width: 2.4 },
				{ label: "Value", width: 1.2, align: "right" },
				{ label: "Status", width: 1.3 },
				{ label: "Quote ID", width: 2.2 },
			],
			result.quotes.map((quote) => [formatShortDate(quote.created), quote.kind, quote.location, quote.value === null ? "" : formatMoney(quote.value), quote.status, quote.id])
		)
	}

	if (result.directBookings.length > 0) {
		writer.subheading("Online bookings taken on the website")
		writer.table(
			[
				{ label: "Created", width: 1.5 },
				{ label: "Destination", width: 2.4 },
				{ label: "Travel date", width: 1.4 },
				{ label: "Total", width: 1.2, align: "right" },
				{ label: "Paid", width: 1.2, align: "right" },
				{ label: "Status", width: 1.4 },
			],
			result.directBookings.map((booking) => [formatShortDate(booking.created), booking.location, formatShortDate(booking.travelDate), formatMoney(booking.total), formatMoney(booking.paidByClient), booking.paidStatus])
		)
	}

	if (result.giftVouchers.length > 0) {
		writer.subheading("Gift vouchers")
		writer.table(
			[
				{ label: "Created", width: 1.5 },
				{ label: "Type", width: 1.6 },
				{ label: "Value", width: 1.2, align: "right" },
				{ label: "Status", width: 1.4 },
				{ label: "Bought by", width: 2 },
			],
			result.giftVouchers.map((voucher) => [formatShortDate(voucher.created), voucher.type, voucher.value === null ? "" : formatMoney(voucher.value), voucher.status, [voucher.firstName, voucher.lastName].filter(Boolean).join(" ")])
		)
	}

	// Correspondence
	writer.heading("Email correspondence held on file")

	if (!result.correspondence.accessible) {
		writer.text("The correspondence archive could not be read with the account running this report, so no conclusion is drawn either way for this address.", { size: 9.5, colour: MUTED })
	} else {
		const messages = result.correspondence.messages
		const shown = messages.slice(0, 40)

		writer.table(
			[
				{ label: "Date", width: 1.6 },
				{ label: "Direction", width: 1 },
				{ label: "From", width: 2.2 },
				{ label: "Subject", width: 4 },
			],
			shown.map((message) => [formatShortDateTime(message.occurredAt), message.direction, message.from, message.subject]),
			{ emptyMessage: noneFound("No email correspondence involving this address was found in the archive.") }
		)

		if (messages.length > shown.length) {
			writer.text(`${messages.length - shown.length} further message(s) are listed in full on the Correspondence sheet of the accompanying spreadsheet.`, { size: 9, colour: MUTED })
		}
	}

	const related = result.mailchimp?.related || []

	if (related.length > 0) {
		writer.heading("Possible related contacts")
		writer.text("Mailchimp's search also returned the following contacts as loose matches. They are different email addresses and are recorded only for completeness; nothing in this report treats them as belonging to the subject.", { size: 9.5, colour: MUTED })
		writer.space(4)
		writer.table(
			[
				{ label: "Contact", width: 3 },
				{ label: "Status", width: 1.4 },
				{ label: "Audience ID", width: 2 },
			],
			related.map((contact) => [contact.email, contact.status, contact.listId])
		)
	}
}

/**
 * Build the whole report and return it as a blob ready to write to disk.
 */
export const buildReportPDF = async (results, runInfo) => {
	const document = await PDFDocument.create()

	document.setTitle(`${runInfo.caseReference} - email address lookup report`)
	document.setSubject("Marketing, enquiry and booking records held against a list of email addresses")
	document.setProducer("Golf Holidays Direct booking system")
	document.setCreationDate(new Date(runInfo.generatedAt))

	const fonts = {
		regular: await document.embedFont(StandardFonts.Helvetica),
		bold: await document.embedFont(StandardFonts.HelveticaBold),
	}

	const writer = new Writer(document, fonts)

	// Cover
	writer.space(40)
	writer.title("Email address lookup report")
	writer.text(runInfo.caseReference, { size: 12, colour: MUTED })
	writer.space(18)

	const withMemberships = results.filter((result) => (result.mailchimp?.memberships || []).length > 0)
	const subscribed = results.filter((result) => (result.mailchimp?.memberships || []).some((membership) => membership.status === "subscribed"))
	const withAccounts = results.filter((result) => result.clients.length > 0)
	const withBookings = results.filter((result) => result.bookings.length > 0)
	const totalValue = results.reduce((total, result) => total + result.totals.lifetimeValue, 0)

	writer.keyValues([
		["Generated", formatDateTime(runInfo.generatedAt)],
		["Prepared by", runInfo.operator],
		["Source file", `${runInfo.sourceFile} (${runInfo.sourceRows} row(s) read)`],
		["Addresses in scope", String(results.length)],
		["Mailchimp account", runInfo.mailchimpAccount || "Not connected"],
		["Audiences searched", runInfo.audienceNames.length > 0 ? runInfo.audienceNames.join(", ") : "None"],
	])

	writer.heading("Findings at a glance")
	writer.keyValues([
		["Addresses found on a mailing list", `${withMemberships.length} of ${results.length}`],
		["Currently subscribed", `${subscribed.length} of ${results.length}`],
		["Addresses with a booking system account", `${withAccounts.length} of ${results.length}`],
		["Addresses with at least one booking", `${withBookings.length} of ${results.length}`],
		["Combined lifetime value", formatMoney(totalValue)],
		["Addresses that could not be completed", String(results.filter((result) => result.status === "ERROR").length)],
	])

	writer.heading("What was searched")
	writer.bullets(runInfo.sourcesSearched)

	writer.heading("Method and limitations")
	writer.bullets(runInfo.limitations)

	writer.heading("Contents")
	writer.table(
		[
			{ label: "Email address", width: 3.4 },
			{ label: "Subscription", width: 2 },
			{ label: "Accounts", width: 0.9, align: "right" },
			{ label: "Enquiries", width: 0.9, align: "right" },
			{ label: "Bookings", width: 0.9, align: "right" },
			{ label: "Lifetime value", width: 1.5, align: "right" },
		],
		results.map((result) => {
			// A failed lookup shows dashes, never zeros: nothing was established
			const failed = result.status === "ERROR"

			return [
				result.email,
				subscriptionSummary(result, { short: true }),
				failed && result.clients.length === 0 ? "-" : String(result.clients.length),
				failed && result.enquiries.length === 0 ? "-" : String(result.enquiries.length),
				failed && result.bookings.length === 0 ? "-" : String(result.bookings.length),
				failed && result.bookings.length === 0 ? "-" : formatMoney(result.totals.lifetimeValue),
			]
		})
	)

	results.forEach((result) => writeSubject(writer, result))

	writer.finish(runInfo.caseReference)

	const bytes = await document.save()

	return new Blob([bytes], { type: "application/pdf" })
}
